import React from "react";
import { motion, AnimatePresence, useInView } from "motion/react";
import { capturePosthog } from "@/lib/posthog";
import { DashboardScene } from "./scenes/DashboardScene";
import { PlanningScene } from "./scenes/PlanningScene";
import { ReportScene } from "./scenes/ReportScene";
import { RegistryScene } from "./scenes/RegistryScene";
import { LeavesScene } from "./scenes/LeavesScene";

import {
  DashboardIcon,
  PlanningTeamIcon,
  ReportsIcon,
  RegisterIcon,
  LeavesIcon,
} from "@/components/icons/nav";
import { Bell, ChevronDown, LogOut, Menu } from "lucide-react";

type SceneId = "dashboard" | "planning" | "report" | "registry" | "leaves";

const SCENES: {
  id: SceneId;
  label: string;
  shortLabel: string;
  path: string;
  icon: React.ElementType;
  duration: number;
}[] = [
  { id: "dashboard", label: "Tableau de bord", shortLabel: "Suivi", path: "/dashboard", icon: DashboardIcon, duration: 8000 },
  { id: "planning", label: "Planning équipe", shortLabel: "Planning", path: "/planning", icon: PlanningTeamIcon, duration: 9000 },
  { id: "report", label: "Rapports", shortLabel: "Rapports", path: "/reports", icon: ReportsIcon, duration: 8000 },
  { id: "registry", label: "Registre du personnel", shortLabel: "Personnel", path: "/employees", icon: RegisterIcon, duration: 8500 },
  { id: "leaves", label: "Congés du magasin", shortLabel: "Congés", path: "/dashboard/conges", icon: LeavesIcon, duration: 7500 },
];

const RESUME_DELAY = 12000;

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
};

const usePageVisible = () => {
  const [visible, setVisible] = React.useState(() => typeof document === "undefined" || !document.hidden);
  React.useEffect(() => {
    const handler = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);
  return visible;
};

export const InteractiveDemo: React.FC = () => {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [autoplay, setAutoplay] = React.useState(true);
  const [cycleKey, setCycleKey] = React.useState(0);
  const reducedMotion = usePrefersReducedMotion();
  const frameRef = React.useRef<HTMLDivElement>(null);
  // Hors de l'écran ou onglet masqué, la démo se met en pause : plus de scènes rejouées
  // (ni de minuteurs, ni d'animations) pendant que le visiteur lit la suite de la page.
  const inView = useInView(frameRef, { amount: 0.25 });
  const pageVisible = usePageVisible();
  const running = autoplay && inView && pageVisible;

  const active = SCENES[activeIdx];

  React.useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => {
      setActiveIdx((i) => (i + 1) % SCENES.length);
      setCycleKey((k) => k + 1);
    }, active.duration);
    return () => clearTimeout(t);
  }, [activeIdx, running, active.duration]);

  React.useEffect(() => {
    if (autoplay) return;
    const t = setTimeout(() => setAutoplay(true), RESUME_DELAY);
    return () => clearTimeout(t);
  }, [autoplay, activeIdx]);

  const handleSelect = (idx: number) => {
    setActiveIdx(idx);
    setAutoplay(false);
    setCycleKey((k) => k + 1);
    capturePosthog("landing_demo_scene_clicked", { scene_id: SCENES[idx].id });
  };

  React.useEffect(() => {
    capturePosthog("landing_demo_scene_viewed", { scene_id: active.id, autoplay });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.id]);

  // La jauge de progression repart de zéro à chaque reprise, en phase avec le minuteur.
  const showProgress = running && !reducedMotion;

  return (
    <div ref={frameRef} className="relative max-w-[1180px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 bg-card border border-white/60 rounded-[2rem] shadow-cinematic-xl overflow-hidden"
      >
        {/* AppShell-like horizontal header (mimics src/components/layout/AppShell.tsx) */}
        <div className="app-shell-glass bg-background/80 border-b">
          <div className="px-3 md:px-5">
            <div className="flex items-center justify-between h-12 md:h-14 gap-2 md:gap-3">
              {/* Left: logo + store chip + inline nav */}
              <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                <div aria-hidden className="flex items-center gap-2 shrink-0">
                  <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-[12px] font-bold text-primary-foreground shadow-sm">
                    E
                  </div>
                  <span className="text-[14px] font-bold tracking-tight text-foreground hidden sm:block">
                    Equiply
                  </span>
                </div>

                <span aria-hidden className="hidden md:inline text-muted-foreground select-none">·</span>

                {/* StoreChip mock — visible on mobile too, like the real app (décor : pas un vrai bouton) */}
                <div aria-hidden className="flex items-center gap-1.5 md:gap-2 h-7 md:h-8 px-2 md:px-2.5 rounded-lg bg-muted/40 border border-border/40 text-[11px] md:text-[12px] font-medium text-foreground shrink-0 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-success shrink-0" />
                  <span className="truncate max-w-[110px] sm:max-w-[140px]">Carrefour Express Centre</span>
                  <ChevronDown className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground shrink-0" />
                </div>

                {/* Inline nav (desktop only) — items = scenes */}
                <nav role="tablist" aria-label="Sections de la démo" className="hidden lg:flex items-center ml-1 gap-0.5 min-w-0 overflow-hidden">
                  {SCENES.map((s, i) => {
                    const Icon = s.icon;
                    const isActive = i === activeIdx;
                    return (
                      <button
                        key={s.id}
                        role="tab"
                        aria-selected={isActive}
                        aria-controls="landing-demo-panel"
                        onClick={() => handleSelect(i)}
                        className={`relative flex items-center gap-1.5 h-9 px-3 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                        }`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span>{s.shortLabel}</span>
                        {isActive && showProgress && (
                          <motion.span
                            key={`progress-${cycleKey}`}
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: active.duration / 1000, ease: "linear" }}
                            className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary origin-left rounded-full"
                          />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Right: notif + avatar + logout (desktop) | hamburger (mobile) — décor, hors tabulation */}
              <div aria-hidden className="flex items-center gap-1 md:gap-1.5 shrink-0">
                <span className="relative p-1.5 rounded-lg text-muted-foreground">
                  <Bell className="w-4 h-4" strokeWidth={1.75} />
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-destructive rounded-full" />
                </span>
                {/* Avatar en initiales, comme dans l'appli : plus d'image tierce (pravatar.cc) chargée sur l'accueil */}
                <span className="w-7 h-7 rounded-full border border-primary/20 bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">
                  S
                </span>
                <span className="hidden lg:flex p-1.5 rounded-lg text-muted-foreground">
                  <LogOut className="w-4 h-4" strokeWidth={1.75} />
                </span>
                {/* Hamburger menu — mimics the real app's <lg behavior */}
                <span className="lg:hidden p-1.5 rounded-lg text-muted-foreground">
                  <Menu className="w-5 h-5" strokeWidth={1.75} />
                </span>
              </div>
            </div>
          </div>

          {/* Scene tabs strip (mobile + tablet) — under the header, scrollable */}
          <div className="lg:hidden border-t border-border/40 overflow-x-auto scrollbar-none">
            <div role="tablist" aria-label="Sections de la démo" className="flex gap-1 px-2 py-1.5 min-w-max">
              {SCENES.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === activeIdx;
                return (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="landing-demo-panel"
                    onClick={() => handleSelect(i)}
                    className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
                      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {s.shortLabel}
                    {isActive && showProgress && (
                      <motion.span
                        key={`progress-m-${cycleKey}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: active.duration / 1000, ease: "linear" }}
                        className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary origin-left rounded-full"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main content — fixed height with internal scroll on all breakpoints */}
        <div className="h-[560px] md:h-[600px] bg-muted/10 overflow-hidden relative">
          <div
            id="landing-demo-panel"
            role="tabpanel"
            aria-label={`Aperçu animé de l'écran « ${active.label} »`}
            className="absolute inset-0 overflow-y-auto"
          >
            <AnimatePresence mode="wait">
              {/* La maquette est une illustration : ses faux boutons restent hors de la tabulation et des lecteurs d'écran. */}
              <motion.div
                key={`${active.id}-${cycleKey}`}
                inert
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                {active.id === "dashboard" && <DashboardScene reducedMotion={reducedMotion} />}
                {active.id === "planning" && <PlanningScene reducedMotion={reducedMotion} />}
                {active.id === "report" && <ReportScene reducedMotion={reducedMotion} />}
                {active.id === "registry" && <RegistryScene reducedMotion={reducedMotion} />}
                {active.id === "leaves" && <LeavesScene reducedMotion={reducedMotion} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
